/*!
 * bb-search-analytics.js - BeanBurds search telemetry
 *
 * Records what people search for, whether the dropdown gave them anything, and
 * what they clicked. Feeds search.events in Supabase via the search-events
 * edge function.
 *
 * DESIGN RULE: this file observes, it never participates. It does not patch
 * fetch, does not touch bb-smart-search.js internals, does not preventDefault,
 * and never throws into a handler the search relies on. If every line of this
 * file failed, search would behave exactly as it does today.
 *
 * Privacy: no name, email, customer id or IP is collected. session_id is a
 * random uuid held in sessionStorage and dies with the tab. Rows are kept
 * indefinitely (the 90-day purge was removed on 13 Sep 2026).
 *
 * ---------------------------------------------------------------------------
 * 2026-09-13 - two measurement bugs fixed. Do not reintroduce either.
 *
 * 1. UNKNOWN IS NOT ZERO.
 *    The old line was:
 *        var counted = dd ? countResults(dd) : { total: 0, kinds: {} };
 *    so any time the dropdown could not be seen at read time, the search was
 *    filed as a zero-result search. It is read up to 3.4s after the last
 *    keystroke (settleMs + renderMaxMs), and a shopper who taps a result inside
 *    that window closes the panel first - so fast, successful searches were
 *    being recorded as failures. "xbloom" was logged 122 times with up to 10
 *    results and once with zero; "Acce" was logged with zero results while the
 *    same search_id carried a click on result #2.
 *    Now: results_count is null when we could not measure, and null never
 *    counts as a zero anywhere downstream. A genuine zero is still a real 0,
 *    because bb-smart-search keeps the panel VISIBLE and renders the theme's
 *    .predictive-search-results__no-results block - only close() hides it.
 *
 * 2. SUGGESTION PILLS WERE INVISIBLE.
 *    bb-smart-search sets the pill's class to the modifier alone:
 *        li.className = 'predictive-search-results__card--query';
 *    with no base .predictive-search-results__card. So a listener bound to the
 *    base class never fired for pills, and a search where the shopper clicked a
 *    brand/collection/guide pill looked like an abandon. That is a large part
 *    of the 67% no-click rate. Pills are now tracked as clicks (clicked_type
 *    "pill-*"), while still being excluded from results_count - they are
 *    navigation, not results.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__bbSearchAnalytics) return;
  window.__bbSearchAnalytics = true;

  var ENDPOINT =
    window.BB_SEARCH_EVENTS_URL ||
    'https://ocwgdcygumldlefndzcm.supabase.co/functions/v1/search-events';

  var CFG = {
    settleMs: 900,      // typing pause that counts as "they meant this"
    renderWaitMs: 400,  // quiet period in the dropdown before counting results
    renderMaxMs: 2500,  // give up waiting and count what is there
    sampleMinMs: 100,   // floor between dropdown samples; mutations are dense
    minChars: 2,        // matches bb-smart-search CFG.minChars
    flushAt: 8,
    flushMs: 5000,
    debug: /[?&]bb_debug=1/.test(location.search)
  };

  /* Product cards carry the base class. Pills carry ONLY the --query modifier,
   * so both have to be named explicitly or pills are never seen. */
  var SEL_RESULT = '.predictive-search-results__card';
  var SEL_PILL   = '.predictive-search-results__card--query';
  var SEL_ANY    = SEL_RESULT + ', ' + SEL_PILL;

  function log() {
    if (CFG.debug) console.log.apply(console, ['[bb-analytics]'].concat([].slice.call(arguments)));
  }

  /* Nothing in here may ever surface as an error in the search path. */
  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (e) { log('swallowed', e); }
    };
  }

  function uuid() {
    try {
      if (crypto && crypto.randomUUID) return crypto.randomUUID();
    } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : ((r & 0x3) | 0x8)).toString(16);
    });
  }

  var sessionId = (function () {
    try {
      var k = 'bb_search_sid';
      var v = sessionStorage.getItem(k);
      if (!v) { v = uuid(); sessionStorage.setItem(k, v); }
      return v;
    } catch (e) { return uuid(); }
  })();

  var device = (function () {
    var w = window.innerWidth || 1024;
    return w < 750 ? 'mobile' : w < 1100 ? 'tablet' : 'desktop';
  })();

  var locale = (document.documentElement.getAttribute('lang') || '').slice(0, 12) || null;

  /* ------------------------------------------------------------------ queue */

  var queue = [];
  var flushTimer = null;

  function enqueue(ev) {
    ev.session_id = sessionId;
    ev.ts = Date.now();
    ev.device = device;
    ev.locale = locale;
    ev.page_path = (location.pathname + location.search).slice(0, 300);
    ev.source = 'storefront';
    queue.push(ev);
    log('queued', ev.kind, ev.q || ev.clicked_handle || '');
    if (queue.length >= CFG.flushAt) flush();
    else if (!flushTimer) flushTimer = setTimeout(flush, CFG.flushMs);
  }

  function flush(useBeacon) {
    if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
    if (!queue.length) return;
    var batch = queue.splice(0, 50);
    var body = JSON.stringify({ events: batch });

    // sendBeacon survives the page going away; fetch does not.
    if (useBeacon && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
        return;
      } catch (e) { /* fall through to fetch */ }
    }
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: body,
        keepalive: true,
        credentials: 'omit',
        mode: 'cors'
      }).catch(function () {});
    } catch (e) {}
  }

  /* ------------------------------------------------- the pending search slot
   *
   * A person typing "moccamaster" settles on "moc", "mocca", "moccamaster".
   * Only the last one is a real query. So a settled search is held here and is
   * only sent once it is clear the person stopped extending it: a click, a
   * submit, a genuinely different query, or the page going away.
   */

  var pending = null;   // { ev, searchId, qNorm }
  var current = null;   // the search_id clicks and submits attach to

  /* One "episode" is one settled act of typing. The counter exists so that a
   * click which forces a search row out early can tell the still-in-flight
   * settle callback to stay quiet rather than emit a duplicate. */
  var episode = 0;
  var forcedEpisode = -1;
  var lastTyped = null;   // { q, qNorm } - the newest query, settled or not

  function normalise(s) {
    if (!s) return '';
    return s.toString().toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[آأإٱ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/ى/g, 'ي')
      .replace(/[ً-ْـٰ]/g, '')
      .replace(/[^a-z0-9؀-ۿ]+/g, ' ')
      .replace(/\s+/g, ' ').trim();
  }

  function commitPending() {
    if (!pending) return;
    enqueue(pending.ev);
    current = {
      searchId: pending.searchId,
      q: pending.ev.q,
      acted: false,
      results: pending.ev.results_count   // may be null = never measured
    };
    pending = null;
  }

  /* A new settled query supersedes the pending one only if it extends it. */
  function offerSettled(ev, qNorm) {
    if (pending && qNorm.indexOf(pending.qNorm) === 0) {
      // Still the same act of typing. Replace, keeping the same search_id so a
      // click that has already happened still lines up.
      ev.search_id = pending.searchId;
      pending = { ev: ev, searchId: pending.searchId, qNorm: qNorm };
      return;
    }
    commitPending();
    pending = { ev: ev, searchId: ev.search_id, qNorm: qNorm };
  }

  /* ------------------------------------------------------- reading the DOM */

  function visibleDropdown() {
    var panels = document.querySelectorAll('.predictive-search-dropdown');
    for (var i = 0; i < panels.length; i++) {
      var p = panels[i];
      var host = p.closest('.bb-ss-panel') || p;
      if (host.hidden) continue;
      if (p.getClientRects().length) return p;
    }
    return null;
  }

  function cardType(card) {
    var m = (card.className || '').match(/predictive-search-results__card--([a-z]+)/);
    if (m) return m[1];
    var a = card.querySelector && card.querySelector('a[href]');
    var href = a ? a.getAttribute('href') || '' : '';
    if (href.indexOf('/products/') > -1) return 'product';
    if (href.indexOf('/collections/') > -1) return 'collection';
    if (href.indexOf('/blogs/') > -1) return 'article';
    return 'other';
  }

  /* Counts real results only. Pills are deliberately absent: they lack the base
   * class, so this selector cannot see them, and that is correct - a brand
   * suggestion is navigation, not a result. The --query guard below is kept as
   * belt and braces in case the theme ever adds the base class to a pill. */
  function countResults(dd) {
    var cards = dd.querySelectorAll(SEL_RESULT);
    var kinds = {};
    var total = 0;
    for (var i = 0; i < cards.length; i++) {
      var t = cardType(cards[i]);
      if (t === 'query') continue;
      kinds[t] = (kinds[t] || 0) + 1;
      total++;
    }
    return { total: total, kinds: kinds };
  }

  /* Results arrive asynchronously. Wait for the dropdown to stop changing, then
   * report what it showed.
   *
   * cb receives either a {total, kinds} reading or NULL meaning "could not
   * measure". Null is not zero. The last sighting during the wait is kept, so a
   * shopper who clicks a result before the timer expires still has their result
   * count recorded rather than being filed as a zero.
   *
   * done() must fire exactly once. Both the quiet timer and the hard cap can
   * reach it, and an unguarded second call re-reads the dropdown later, by
   * which time it may be showing the results of a completely different query. */
  function whenSettled(cb) {
    var start = Date.now();
    var quiet = null;
    var obs = null;
    var finished = false;
    var lastSeen = null;     // last reading taken while the dropdown was open
    var lastSample = 0;

    function sample() {
      var now = Date.now();
      if (now - lastSample < CFG.sampleMinMs) return;   // mutations are dense
      lastSample = now;
      var dd = visibleDropdown();
      if (dd) lastSeen = countResults(dd);
    }

    function done() {
      if (finished) return;
      finished = true;
      if (obs) { obs.disconnect(); obs = null; }
      if (quiet) { clearTimeout(quiet); quiet = null; }
      var dd = visibleDropdown();
      cb(dd ? countResults(dd) : lastSeen);
    }

    function bump() {
      if (finished) return;
      sample();
      if (quiet) clearTimeout(quiet);
      if (Date.now() - start > CFG.renderMaxMs) return done();
      quiet = setTimeout(done, CFG.renderWaitMs);
    }

    try {
      obs = new MutationObserver(bump);
      obs.observe(document.body, { childList: true, subtree: true });
    } catch (e) {}
    bump();
    setTimeout(done, CFG.renderMaxMs);
  }

  /* --------------------------------------------------------- input tracking */

  var settleTimer = null;

  function isSearchInput(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    return el.name === 'q' || el.type === 'search';
  }

  document.addEventListener('input', safe(function (e) {
    var input = e.target;
    if (!isSearchInput(input)) return;

    var q = (input.value || '').trim();
    if (settleTimer) clearTimeout(settleTimer);
    if (q.length < CFG.minChars) { lastTyped = null; return; }

    lastTyped = { q: q, qNorm: normalise(q) };
    var myEpisode = ++episode;

    settleTimer = setTimeout(safe(function () {
      settleTimer = null;
      if ((input.value || '').trim() !== q) return;   // they kept typing
      var qNorm = normalise(q);
      if (!qNorm) return;

      whenSettled(safe(function (counted) {
        // A click already forced this episode's row out, with a live reading of
        // the dropdown. Emitting here too would double-count the search.
        if (myEpisode === forcedEpisode) return;
        offerSettled({
          kind: 'search',
          search_id: uuid(),
          q: q,
          // null, not 0, when the dropdown was never seen. The ingest function
          // preserves the null; it used to coerce it to 0 via Number(null).
          results_count: counted ? counted.total : null,
          result_kinds: counted ? counted.kinds : null
        }, qNorm);
      }));
    }), CFG.settleMs);
  }), true);

  /* --------------------------------------------------------- click tracking */

  /* A click can land before the search row exists.
   *
   * The row is only produced settleMs after the last keystroke plus however
   * long whenSettled waits - up to 3.4s. A shopper who types and clicks inside
   * that window used to get NOTHING recorded: no search row, so no click row
   * either, so the search silently became an abandon. Since they just clicked
   * inside the dropdown, it is on screen and can be counted right now, which is
   * a better reading than the timer would have got anyway.
   *
   * Returns false only when there is nothing sensible to attach to. */
  function ensureCurrent(dd) {
    commitPending();
    if (current) return true;
    if (!lastTyped) return false;

    var counted = countResults(dd);
    var sid = uuid();
    forcedEpisode = episode;                       // silence the in-flight settle
    if (settleTimer) { clearTimeout(settleTimer); settleTimer = null; }
    enqueue({
      kind: 'search',
      search_id: sid,
      q: lastTyped.q,
      results_count: counted.total,
      result_kinds: counted.kinds
    });
    current = { searchId: sid, q: lastTyped.q, acted: false, results: counted.total };
    return true;
  }

  /* Shared by mouse clicks and by Enter on a keyboard-highlighted card, which
   * navigates via location.href and fires no click event of its own. */
  function recordCardActivation(card) {
    var dd = card.closest('.predictive-search-dropdown');
    if (!dd) return;

    var type = cardType(card);
    var isPill = type === 'query';

    if (!ensureCurrent(dd)) return;
    if (current.acted) return;

    var link = card.querySelector('a[href]');
    var href = link ? link.getAttribute('href') || '' : '';
    var m = href.match(/\/(?:products|collections|blogs\/[^/]+)\/([^?#]+)/);
    var handle = '';
    if (m) { try { handle = decodeURIComponent(m[1]); } catch (err) { handle = m[1]; } }

    // Position among real results, in rendered order, 1-based. A pill sits
    // outside that list, so it has no position.
    var pos = null;
    if (!isPill) {
      var all = dd.querySelectorAll(SEL_RESULT);
      var n = 0;
      for (var i = 0; i < all.length; i++) {
        if (cardType(all[i]) === 'query') continue;
        n++;
        if (all[i] === card) { pos = n; break; }
      }
    }

    var titleEl = card.querySelector('.resource-card__title') || link;
    var title = titleEl ? (titleEl.textContent || '').trim().slice(0, 200) : '';

    // A pill is a brand / collection / guide suggestion. Recording it as a
    // click is what stops these searches being counted as abandons; the
    // "pill-" prefix keeps them separable from real result clicks.
    var clickedType = type;
    if (isPill) {
      clickedType =
        href.indexOf('/collections/') > -1 ? 'pill-collection' :
        href.indexOf('/products/') > -1    ? 'pill-product'    :
        href.indexOf('/blogs/') > -1       ? 'pill-article'    : 'pill';
    }

    current.acted = true;
    enqueue({
      kind: 'click',
      search_id: current.searchId,
      clicked_type: clickedType,
      clicked_handle: handle,
      clicked_title: title,
      clicked_pos: pos
    });
    flush(true);                                  // they are navigating away
  }

  document.addEventListener('click', safe(function (e) {
    var card = e.target.closest && e.target.closest(SEL_ANY);
    if (card) recordCardActivation(card);
  }), true);

  /* -------------------------------------------------- submit / enter to /search */

  document.addEventListener('submit', safe(function (e) {
    var form = e.target;
    if (!form || !form.querySelector) return;
    var input = form.querySelector('input[name="q"], input[type="search"]');
    if (!input) return;
    var q = (input.value || '').trim();
    if (q.length < CFG.minChars) return;

    commitPending();
    if (current && normalise(current.q) === normalise(q)) {
      current.acted = true;
      enqueue({ kind: 'submit', search_id: current.searchId, q: q });
    } else {
      enqueue({ kind: 'submit', search_id: uuid(), q: q });
    }
    flush(true);
  }), true);

  /* ------------------------------------------------------------- abandonment
   *
   * A query that produced results, was never clicked, and never went through to
   * the results page. This is the signal that says "we showed them something
   * and it wasn't what they wanted".
   *
   * current.results may be null (never measured). null > 0 is false, so an
   * unmeasured search is never called an abandon - we do not know that it
   * showed them anything.
   */

  function markAbandon() {
    if (current && !current.acted && current.results > 0) {
      current.acted = true;
      enqueue({ kind: 'abandon', search_id: current.searchId, q: current.q });
    }
  }

  document.addEventListener('keydown', safe(function (e) {
    if (e.key === 'Escape') { commitPending(); markAbandon(); return; }

    // Arrow-key navigation then Enter: bb-smart-search sets location.href
    // itself, so no click event ever reaches us. Read the highlighted card
    // before it goes. Listening on keydown/capture means this runs before the
    // theme's own handler; we only read, so ordering is safe either way.
    // bb-smart-search treats product cards and pills as one selectable set, so
    // both are checked here.
    if (e.key === 'Enter') {
      var dd = visibleDropdown();
      if (!dd) return;
      var card = dd.querySelector(SEL_RESULT + '.keyboard-focus') ||
                 dd.querySelector(SEL_PILL + '.keyboard-focus') ||
                 dd.querySelector(SEL_RESULT + '[aria-selected="true"]') ||
                 dd.querySelector(SEL_PILL + '[aria-selected="true"]');
      if (card) recordCardActivation(card);
    }
  }), true);

  window.addEventListener('pagehide', safe(function () {
    commitPending();
    markAbandon();
    flush(true);
  }));

  document.addEventListener('visibilitychange', safe(function () {
    if (document.visibilityState === 'hidden') {
      commitPending();
      markAbandon();
      flush(true);
    }
  }));

  // A search the person typed and then walked away from without any of the
  // above firing still lands, because pagehide always runs.
  log('ready', { session: sessionId, endpoint: ENDPOINT });
})();
